import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Typography, Grid, Avatar, IconButton,
} from '@material-ui/core';
import {
    Edit, InfoOutlined,
} from '@material-ui/icons';
import './SchedulePeriodSummary.scss';
import ChipCompletedStep from '@commonClient2/components/ChipCompletedStep';
import { UPDATE_DRAFT_BID_PROFILE_OBJECTS } from '../../../store/bidProfileDraft';
import { UPDATE_BID_PROFILE_OBJECTS } from '../../../store/bidProfile';
import DateFormatService from '../../../../services/DateFormatService';
import {
    BidProfileEditTab, UkgDateFormat, EmployeeSubmissionTimeUnit, BidProcessType,
} from '../../../../Enums';
import { getBidTimePeriodLabel } from './SchedulePeriodSummary.service';

/**
 * @param {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile | undefined} profile
 */
const isActiveBidForever = (profile) => (profile?.originalScheduleTemplates.isActiveWorkplanBid && profile?.originalScheduleTemplates.activeWorkplanEndDate === 32503680000)
    || (profile?.isRebid && profile.isForeverRequestPeriod);

function SchedulePeriodSummary() {
    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { bidProfileDraft, bidProfile } = useSelector((state) => state);
    const { t } = useTranslation('shiftBidding');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    /**
     * @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile | undefined}
     */
    let profile;
    let isDraft = false;
    let handleEdit = () => { };
    if (bidProfileDraft.id !== '') {
        profile = bidProfileDraft;
        isDraft = true;

        handleEdit = () => {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE_OBJECTS,
                payload: {
                    general: {
                        tabSelected: BidProfileEditTab.Form,
                    },
                    bidProfileInformation: {
                        isExpanded: false,
                    },
                    scheduleTemplates: {
                        isExpanded: false,
                    },
                    employees: {
                        isExpanded: false,
                    },
                    schedule: {
                        isExpanded: true,
                    },
                },
            });
            navigate('/Manager/form');
        };
    } else if (bidProfile.id !== '') {
        profile = bidProfile;
        isDraft = false;

        handleEdit = () => {
            dispatch({
                type: UPDATE_BID_PROFILE_OBJECTS,
                payload: {
                    general: {
                        tabSelected: BidProfileEditTab.Form,
                    },
                    bidProfileInformation: {
                        isExpanded: false,
                    },
                    scheduleTemplates: {
                        isExpanded: false,
                    },
                    employees: {
                        isExpanded: false,
                    },
                    schedule: {
                        isExpanded: true,
                    },
                },
            });
            navigate('/Manager/form');
        };
    }

    // @ts-ignore
    if (profile === undefined) {
        return <></>;
    }

    const {
        originalBidProfileInformation,
        originalSchedule,
        schedule,
        isCreationAllowed,
        isViewAllowed,
    } = profile;

    const {
        dayOfTheWeek,
        startBiddingTime,
        endBiddingTime,
        timePerEmployeeValue,
        timePerEmployeeUnit,
        submissionPeriodStartDate,
        submissionPeriodEndDate,
        requestPeriodStartDate,
        requestPeriodEndDate,
        isCompleted,
        isAnytime,
    } = originalSchedule;

    const isSequential = originalBidProfileInformation.bidProcessType === BidProcessType.Sequential;
    return (
        <div className="summary-section">
            <div className="summary-section-header">
                <div className="summary-section-avatar-title-wrapper">
                    <Avatar className="bde-avatar">4</Avatar>
                    <Typography variant="h6">{t('form.schedulePeriod.subHeader')}</Typography>
                </div>
                <Grid
                    item
                    className="summary-section-indicator-container"
                >
                    {isDraft ? (
                        <span
                            className="summary-completed-icon-wrapper"
                        >
                            {
                                isCompleted && <ChipCompletedStep />
                            }
                        </span>
                    ) : null}
                    {(isCreationAllowed || isViewAllowed) && (
                        <IconButton
                            color="primary"
                            className="summary-edit-button"
                            onClick={() => handleEdit()}
                        >
                            {(schedule.isEditable && isCreationAllowed) ? <Edit /> : <InfoOutlined />}
                        </IconButton>
                    )}
                </Grid>
            </div>

            <div className="summary-section-content">
                {isSequential && (
                    <>
                        <div>
                            <Typography
                                variant="body1"
                                display="inline"
                            >
                                {t('form.schedulePeriod.applicableDays')}
                                {' : '}
                            </Typography>
                            <Typography
                                variant="body2"
                                display="inline"
                                className="sps-value-container"
                            >
                                {Object.entries(dayOfTheWeek).filter((x) => x[1]).map((entry) => (
                                    `${t(`form.schedulePeriod.sequentialDaysAndHoursStep.dayOfWeek.${entry[0]}`)}`)).join(', ')}
                            </Typography>
                        </div>
                        <div>
                            <Typography
                                variant="body1"
                                display="inline"
                            >
                                {t('form.schedulePeriod.bidTimePeriod')}
                                {' : '}
                            </Typography>
                            <Typography
                                variant="body2"
                                display="inline"
                                className="sps-value-container"
                            >
                                {isAnytime ? t('form.schedulePeriod.sequentialDaysAndHoursStep.anytime') : getBidTimePeriodLabel(startBiddingTime, endBiddingTime, t)}
                            </Typography>
                        </div>
                        <div>
                            <Typography
                                variant="body1"
                                display="inline"
                            >
                                {t('form.schedulePeriod.timePerEmployee.title')}
                                {' : '}
                            </Typography>
                            <Typography
                                variant="body2"
                                display="inline"
                                className="sps-value-container"
                            >
                                {`${timePerEmployeeValue} ${t(
                                    timePerEmployeeUnit === EmployeeSubmissionTimeUnit.Minute
                                        ? 'common.timeUnit.minute'
                                        : 'common.timeUnit.hour',
                                    { count: timePerEmployeeValue },
                                )}`}
                            </Typography>
                        </div>
                    </>
                )}
                <div>
                    <Typography
                        variant="body1"
                        display="inline"
                    >
                        {t('form.schedulePeriod.submissionStartsOn')}
                        {' : '}
                    </Typography>
                    <Typography
                        variant="body2"
                        display="inline"
                        className="sps-value-container"
                    >
                        {DateFormatService.formatDateOrEmpty(
                            submissionPeriodStartDate, 
                            UkgDateFormat.LongDateTime
                        )}
                    </Typography>
                </div>
                <div>
                    <Typography
                        variant="body1"
                        display="inline"
                    >
                        {t('form.schedulePeriod.submissionEndsOn')}
                        {' : '}
                    </Typography>
                    <Typography
                        variant="body2"
                        display="inline"
                        className="sps-value-container"
                    >
                        {DateFormatService.formatDateOrEmpty(submissionPeriodEndDate, UkgDateFormat.LongDateTime)}
                    </Typography>
                </div>
                <div>
                    <Typography
                        variant="body1"
                        display="inline"
                    >
                        {t('form.schedulePeriod.requestPeriod')}
                        {' : '}
                    </Typography>
                    <Typography
                        variant="body2"
                        display="inline"
                        className="sps-value-container"
                    >
                        {
                            isActiveBidForever(profile)
                                // eslint-disable-next-line max-len
                                ? `${DateFormatService.formatDateOrEmpty(requestPeriodStartDate, UkgDateFormat.LongDate)} - ${t('form.schedulePeriod.anytime')}`
                                : DateFormatService.formatPeriod(
                                    requestPeriodStartDate,
                                    requestPeriodEndDate,
                                    UkgDateFormat.LongDate,
                                )
                        }
                    </Typography>
                </div>
            </div>

        </div>
    );
}

export default SchedulePeriodSummary;
